import type { Plan, SubscriptionDetail } from '@/lib/api-client';
import { currentPlanPriceLines } from '@/lib/billing';

interface CurrentPlanPriceProps {
  plan: Plan;
  subscription: Pick<SubscriptionDetail, 'discount'>;
  /**
   * When the next charge happens, already formatted ("Sep 29, 2027"). Null
   * when there is no renewal to price — a cancellation or a scheduled switch
   * owns that line instead (`fallbackLabel`).
   */
  renewsOn: string | null;
  /** The line shown instead of "Renews … at …", e.g. "Cancels Sep 29, 2027". */
  fallbackLabel: string;
}

/**
 * The price block on the Settings current-plan card: what THIS period cost
 * (net of a first-year code like XONERA), its per-month equivalent, what it
 * becomes when the promo ends, and the renewal charge spelled out.
 *
 * It used to quote the catalog list price, so a user who had just paid
 * $119.88 read "$179.88 /year · $14.99/mo, billed yearly" — neither what they
 * paid nor a warning that the renewal costs more.
 */
export function CurrentPlanPrice({
  plan,
  subscription,
  renewsOn,
  fallbackLabel,
}: CurrentPlanPriceProps) {
  const lines = currentPlanPriceLines(plan, subscription);
  return (
    <div data-testid='current-plan-price'>
      <p className='text-2xl font-bold text-foreground'>
        {lines.price}
        <span className='ml-1 text-sm font-normal text-muted-foreground'>
          {lines.unit}
        </span>
      </p>
      {lines.perMonth && (
        <p className='mt-0.5 text-xs text-muted-foreground'>{lines.perMonth}</p>
      )}
      {lines.promo && (
        <p
          data-testid='current-plan-promo'
          className='mt-0.5 text-xs text-muted-foreground'
        >
          {lines.promo}
        </p>
      )}
      <p
        data-testid='current-plan-renewal'
        className='mt-0.5 text-sm text-muted-foreground'
      >
        {renewsOn
          ? `Renews ${renewsOn} at ${lines.renewalAmount}`
          : fallbackLabel}
      </p>
    </div>
  );
}
